import { FC, useEffect, useState, useRef } from "react";
import { cn } from "@heroui/react";

interface PriceChangeProps {
  price: string | null;
  direction: "up" | "down" | null;
  className?: string;
  marketIndex?: number;
}

const PriceChange: FC<PriceChangeProps> = ({
  price,
  direction,
  className,
  marketIndex,
}) => {
  const [color, setColor] = useState<string>("inherit");
  const previousPrice = useRef<string | null>(null);
  const previousDirection = useRef<"up" | "down" | null>(null);
  const previousMarketIndex = useRef<number | undefined>(undefined);
  const timersRef = useRef<number[]>([]);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    const priceChanged = price !== previousPrice.current;
    const directionChanged = direction !== previousDirection.current;
    const marketIndexChanged = marketIndex !== previousMarketIndex.current;

    // If market index changed, reset tracking (new market, don't animate on first render)
    if (marketIndexChanged && previousMarketIndex.current !== undefined) {
      previousPrice.current = price;
      previousDirection.current = direction;
      previousMarketIndex.current = marketIndex;
      return;
    }

    // On first render with valid props, just initialize
    if (previousPrice.current === null && previousDirection.current === null) {
      if (price !== null && direction !== null) {
        previousPrice.current = price;
        previousDirection.current = direction;
        previousMarketIndex.current = marketIndex;
      }
      return;
    }

    // Update refs for comparison
    previousPrice.current = price;
    previousDirection.current = direction;
    previousMarketIndex.current = marketIndex;

    // Only animate if we have valid direction and props actually changed
    // Don't animate if market index just changed (handled above)
    if (
      direction &&
      (priceChanged || directionChanged) &&
      !marketIndexChanged
    ) {
      // Clear any existing timers and RAF
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current = [];

      // Reset to inherit first to start from nothing
      setColor("inherit");

      // Phase 1: Transition to color over 1 second
      // Use requestAnimationFrame to ensure DOM has updated before starting transition
      rafIdRef.current = requestAnimationFrame(() => {
        const timer1 = window.setTimeout(() => {
          const newColor =
            direction === "up" ? "rgb(46, 189, 133)" : "rgb(246, 70, 93)";
          setColor(newColor);

          // Phase 2: Stay with color for 3 seconds
          // Phase 3: Transition back to inherit over 1 second (starts after 1s + 3s = 4s)
          const timer2 = window.setTimeout(() => {
            setColor("inherit");
          }, 3000); // 3s hold, then 1s transition back (handled by CSS transition)

          timersRef.current.push(timer2);
        }, 0); // Start transition immediately after RAF

        timersRef.current.push(timer1);
        rafIdRef.current = null;
      });
    }

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current = [];
    };
  }, [direction, price, marketIndex]);

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <span
        className="whitespace-nowrap"
        style={{
          transition: "color 1000ms ease-out",
          color: color,
        }}
      >
        {price}
      </span>
    </div>
  );
};

export default PriceChange;
